/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, Card, Modal, Input as AntInput, Tag, message, Space } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/formatters";
import { apiClient } from "../../../shared/api/AllApi";
import usePermissions from "../../../hooks/usePermissions";
import { filterBySearch } from "../../../shared/lib/listSearch";
import ListSearch from "../../../shared/ui/ListSearch";
import "../../../shared/styles/ListPages.css";

const TableOrder = () => {
  const { hasPermission } = usePermissions();
  const canCancel = hasPermission("orders.cancel");
  const canViewCancellations = hasPermission("orders.cancellations");
  const [SearchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState("");
  const [selected, setSelected] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    GetApiOrder();
  }, []);

  const GetApiOrder = async () => {
    try {
      const res = await apiClient.get("/orders");

      const formattedData = res.data.map((item) => ({
        id: item.id,
        document_no: item.document_no,
        customer_id: item.customer ? item.customer.id : null,
        total_price: item.total_price,
        amount_paid: item.amount_paid,
        status: item.status || item.list_status || "confirmed",
        cancellation_reason: item.cancellation_reason,
        customer: item.customer ? item.customer.name : t("noCustomer"),
        Type: item.customer ? item.customer.type : t("unknown"),
        Number: item.customer ? item.customer.phone_number : t("noNumber"),
        created_at: item.created_at,
      }));

      setDataSource(formattedData);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    }
  };

  const openCancel = (record) => {
    setSelected(record);
    setCancelNote("");
    setCancelOpen(true);
  };

  const handleCancel = async () => {
    if (!selected?.id) return;
    const note = cancelNote.trim();
    if (note.length < 3) {
      message.warning(t("cancelReasonRequired", "Enter a cancellation reason (min 3 characters)"));
      return;
    }
    setCancelling(true);
    try {
      const res = await apiClient.post(`/orders/${selected.id}/cancel`, { note });
      message.success(
        `${t("orderCancelled", "Order cancelled")} · ${t("refunded", "Refunded")}: ${formatCurrency(res.data?.refund_amount || 0)}`
      );
      setCancelOpen(false);
      setCancelNote("");
      setSelected(null);
      GetApiOrder();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.response?.data?.order?.[0] || t("cancelFailed", "Cancel failed"));
    } finally {
      setCancelling(false);
    }
  };

  const filteredData = useMemo(
    () =>
      filterBySearch(dataSource, SearchText, (item) => [
        item.id,
        item.document_no,
        item.customer,
        item.Number,
        item.Type,
        item.total_price,
        item.amount_paid,
        item.status,
        item.cancellation_reason,
      ]),
    [dataSource, SearchText]
  );

  const columns = [
    {
      title: t("id"),
      key: "index",
      render: (text, record, index) => index + 1,
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("customer")}</span>,
      dataIndex: "customer",
      key: "customer",
      render: (text, record) => (
        record.customer_id ? (
          <Link to={`/GetCustomer/OrderHistory/${record.customer_id}`}>{text}</Link>
        ) : (
          text
        )
      ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("number")}</span>,
      dataIndex: "Number",
      key: "Number",
      render: (text, record) => (
        <Link to={`/TableOrder/InfoOrder/${record.id}`}>{text}</Link>
      ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("type")}</span>,
      dataIndex: "Type",
      key: "Type",
      render: (text, record) => {
        let customerType = t("unknown");
        if (record.Type === "individual") customerType = t("individual");
        else if (record.Type === "company") customerType = t("company");
        else if (record.Type === "organization")
          customerType = t("organization");

        return (
          <Link to={`/TableOrder/InfoOrder/${record.id}`}>{customerType}</Link>
        );
      },
    }, {
      title: <span style={{ fontWeight: "bold" }}>{t("amountPaid")}</span>,
      dataIndex: "amount_paid",
      key: "amount_paid",
      render: (text, record) => (
        <Link to={`/TableOrder/InfoOrder/${record.id}`}>
          {formatCurrency(text)}
        </Link>
      ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("totalPrice")}</span>,
      dataIndex: "total_price",
      key: "total_price",
      render: (text, record) => (
        <Link to={`/TableOrder/InfoOrder/${record.id}`}>
          {formatCurrency(text)}
        </Link>
      ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("status", "Status")}</span>,
      dataIndex: "status",
      key: "status",
      render: (status) =>
        status === "cancelled" ? (
          <Tag color="red">{t("cancelled", "Cancelled")}</Tag>
        ) : (
          <Tag color="green">{t("confirmed", "Confirmed")}</Tag>
        ),
    },
    {
      title: <span style={{ fontWeight: "bold" }}>{t("createdAt")}</span>,
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text ? new Date(text).toLocaleDateString() : "-",
    },
    ...((hasPermission("orders.edit") || canCancel) ? [{
      title: t("actions"),
      key: "actions",
      render: (text, record) => (
        <Space>
          {hasPermission("orders.edit") && record.status !== "cancelled" && (
            <Button
              type="primary"
              onClick={() => navigate(`/edit-order/${record.id}`)}
            >
              {t("edit")}
            </Button>
          )}
          {canCancel && record.status !== "cancelled" && (
            <Button danger onClick={() => openCancel(record)}>
              {t("cancelOrder", "Cancel")}
            </Button>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <div className="list-page">
      <Card
        title={
          <span style={{ fontWeight: "600", fontSize: "1.125rem" }}>
            {t("orders")}
          </span>
        }
        extra={
          canViewCancellations ? (
            <Link to="/cancellations">
              <Button>{t("cancellationsReport", "Cancellations Report")}</Button>
            </Link>
          ) : null
        }
        variant="borderless"
      >
        <ListSearch
          value={SearchText}
          onChange={setSearchText}
          width={360}
          style={{ marginBottom: 16 }}
        />
        <div style={{ overflowX: "auto" }}>
          <Table
            dataSource={filteredData.map((item) => ({ ...item, key: item.id }))}
            columns={columns}
            pagination={{ pageSize: 50 }}
            scroll={{ x: "max-content" }}
            style={{ overflowX: "auto", maxWidth: "100%" }}
            locale={{ emptyText: t("noDataFound") }}
          />
        </div>
      </Card>

      <Modal
        open={cancelOpen}
        title={`${t("cancelOrder", "Cancel Order")} #${selected?.document_no || selected?.id || ""}`}
        okText={t("confirmCancel", "Confirm Cancel")}
        cancelText={t("close", "Close")}
        okButtonProps={{ danger: true, loading: cancelling }}
        onOk={handleCancel}
        onCancel={() => {
          setCancelOpen(false);
          setCancelNote("");
          setSelected(null);
        }}
      >
        <p style={{ marginBottom: 8 }}>
          {t(
            "cancelOrderHint",
            "This will cancel the order and return the products to stock."
          )}
        </p>
        <AntInput.TextArea
          rows={3}
          value={cancelNote}
          onChange={(e) => setCancelNote(e.target.value)}
          placeholder={t("cancelReasonPlaceholder", "Enter cancellation reason...")}
        />
      </Modal>
    </div>
  );
};

export default TableOrder;
